import { useCallback, useEffect, useRef, useState } from "react";
import { fetchPlaybackTicketService } from "../services/playbackService";

/** El backend responde 409 cuando la modelo no está emitiendo. */
const NOT_STREAMING = 409;

/** Cada cuánto se vuelve a mirar si la modelo ya empezó a emitir. */
const OFFLINE_RETRY_MS = 15000;

/**
 * Ciclo de vida del ticket de reproducción.
 *
 * Pide un ticket al entrar a la sala y lo renueva antes de que caduque
 * mientras la pestaña siga abierta, de modo que una sesión larga no se corte
 * a mitad del show. Devuelve la URL ya firmada que consume el reproductor.
 */
export const usePlayback = (modelId, { enabled = true } = {}) => {
    const [playbackUrl, setPlaybackUrl] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const timerRef = useRef(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const request = useCallback(async () => {
        if (!modelId) return;

        setLoading(true);
        try {
            const data = await fetchPlaybackTicketService(modelId);
            if (!mounted.current) return;

            setPlaybackUrl(data.playback_url);
            setError(null);

            // Se renueva con un margen del 20% antes del vencimiento.
            const refreshIn = Math.max(30, (data.expires_in || 900) * 0.8) * 1000;
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(request, refreshIn);
        } catch (err) {
            if (mounted.current) {
                setError(err);
                setPlaybackUrl(null);

                // Que la modelo no esté emitiendo ahora no es un error
                // definitivo: se vuelve a mirar cada poco para que la sala
                // arranque sola en cuanto empiece, en vez de dejar al
                // espectador delante de un aviso hasta que recargue.
                // El resto de errores no se reintentan: un 402 lleva a
                // recargar saldo y un 401 no se arregla esperando.
                if (err?.status === NOT_STREAMING) {
                    clearTimeout(timerRef.current);
                    timerRef.current = setTimeout(request, OFFLINE_RETRY_MS);
                }
            }
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, [modelId]);

    useEffect(() => {
        if (!enabled || !modelId) return undefined;

        request();

        return () => clearTimeout(timerRef.current);
    }, [enabled, modelId, request]);

    // La señal es la única fuente honesta del estado de la sala: el campo
    // `status` de la modelo es una foto del instante en que se cargó la página
    // y se queda obsoleto en cuanto la emisión cae.
    const isLive = Boolean(playbackUrl) && !error;
    const isOffline = error?.status === NOT_STREAMING;

    return { playbackUrl, error, loading, isLive, isOffline, retry: request };
};
